import { useTranslation } from 'react-i18next';
import message from 'antd/es/message';
import cx from 'clsx';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import produce from 'immer';
import { useHistory } from 'react-router-dom';
import type { RcFile } from 'antd/es/upload';

import { IconRcCircleChecked } from '@/assets/icon/common';
import { IconDefaultAvatar, IconDefaultAvatarDark, IconUploadAvatar } from '@/assets/icon/avatar';
import { ImgRcPaidFeature } from '@/assets/pro';
import {
  Button,
  LazyMedia,
  Loading,
  MEDIA_TYPE,
  Modal,
  ImageUpload,
  HelperTooltip,
  SearchInput,
} from '@/components';
import {
  updateUserDetailQuery,
  useUpdateCurrentAddressInfo,
  useTheme,
  useCommonImageUpload,
  usePaidFeatureAvailability,
  PAID_FEATURE,
  useSwitch,
} from '@/hooks';
import { invalidStreamPostList } from '@/views/Stream';
import { CHAINS_DICT, toastError } from '@/utils';

import { AVATAR_ITEM, useQueryNft, useUpdateAvatarUrl } from './hook';
import style from './index.module.scss';

const { CommonModal } = Modal;

interface AvatarModalProps {
  address: string;
  onClose: () => void;
  onChange: (url: string) => void;
  userInfo?: UserInfo;
}

export const SIZE_LIMIT = 5 * 1024 * 1024;
export const PIXEL_LIMIT = 100;

const CheckableIcon = ({
  children,
  checked,
  ...rest
}: {
  children: ReactNode;
  checked?: boolean;
} & JSX.IntrinsicElements['div']) => (
  <div className={style.avatarIcon} {...rest}>
    {children}
    {checked ? <IconRcCircleChecked className={style.checkedIcon} /> : null}
  </div>
);

const SUPPORTED_IMAGES = ['.png', '.jpg', '.jpeg', '.svg'];

export const AvatarModal = ({ onClose, onChange, address, userInfo }: AvatarModalProps) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const history = useHistory();

  const desc = userInfo?.desc;
  const [selected, setSelected] = useState({} as AVATAR_ITEM);
  const { list, isDone } = useQueryNft(address!, '');
  const { mutateAsync: updateAvatarUrl, isLoading: isUpdating } = useUpdateAvatarUrl();
  const update = useUpdateCurrentAddressInfo();
  const { mutateAsync: uploadImage, isLoading: isUploading } = useCommonImageUpload('user_avatar');
  const { data: enableCustomAvatarFeature } = usePaidFeatureAvailability(
    PAID_FEATURE.NON_NFT_AVATAR,
  );

  const { on: imgListShown, turnOn: showImgList, turnOff: hideImgList } = useSwitch();
  const [searchText, setSearchText] = useState<string>();
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>();

  useEffect(() => {
    if (!userInfo?.desc?.user.logo_is_nft && userInfo?.desc?.user.logo_url) {
      const originalCustomAvatar = {
        id: 'custom' as const,
        url: userInfo?.desc?.user.logo_url,
        thumbnail: userInfo?.desc?.user.logo_thumbnail_url,
      };
      setUploadedFile(originalCustomAvatar);
      setSelected(originalCustomAvatar);
    }
  }, [
    userInfo?.desc?.user.logo_is_nft,
    userInfo?.desc?.user.logo_thumbnail_url,
    userInfo?.desc?.user.logo_url,
  ]);

  const [uploadedFile, setUploadedFile] = useState<{
    file?: any;
    id: 'custom';
    url?: string;
    thumbnail?: string;
  } | null>(null);

  const handleAvatarClick = useCallback((e: React.MouseEvent<HTMLImageElement>) => {
    const { dataset } = e.currentTarget;
    setSelected(dataset as AVATAR_ITEM);
  }, []);

  const confirmChange = useCallback(async () => {
    const { thumbnail } = selected;
    try {
      await updateAvatarUrl(selected);
      const isNft = !['default', 'custom'].includes(selected.id);

      const isDefault = selected.id === 'default';
      onChange(thumbnail!);
      update(pre => {
        const next = { ...pre };
        if (next.detail) {
          next.detail = {
            ...next.detail,
            avatar: {
              content_type: MEDIA_TYPE.IMAGE,

              content: isDefault ? '' : selected.url!,
              thumbnail_url: isDefault ? '' : selected.thumbnail,
            },
          };

          if (desc) {
            next.detail.desc = {
              ...desc,
              user: {
                ...desc?.user,
                logo_url: isDefault ? '' : selected.url,
                logo_thumbnail_url: isDefault ? '' : selected.thumbnail,
                logo_is_nft: isNft,
              },
            };
          }
        }

        if (next?.userInfo) {
          next.userInfo = {
            ...next?.userInfo,
          };

          if (desc) {
            next.userInfo.desc = {
              ...desc,
              user: {
                ...desc?.user,
                logo_url: isDefault ? '' : selected.url,
                logo_thumbnail_url: isDefault ? '' : thumbnail,
                logo_is_nft: isNft,
              },
            };
          }
        }

        return next;
      });

      updateUserDetailQuery({
        id: address,
        desc: desc
          ? {
              ...desc,
              user: {
                ...desc?.user,
                logo_url: selected.url,
                logo_thumbnail_url: thumbnail,
                logo_is_nft: isNft,
              },
            }
          : undefined,
      });
      message.success(t('AvatarEditable_AvatarModal.changed'));

      invalidStreamPostList(address);
      onClose();
    } catch (error) {
      toastError(error || t('AvatarEditable_AvatarModal.failed'));
    }
  }, [updateAvatarUrl, selected, onChange, address, desc, onClose, update, t]);

  const gotoPaidFeature = useCallback(() => history.push('/paid'), [history]);

  const handleBeforeUpload = useCallback(
    (file: RcFile) => {
      return new Promise<void>(async (resolve, reject) => {
        if (file.size > SIZE_LIMIT) {
          message.error(t('AvatarEditable_AvatarModal.uploadError'));
          return reject();
        }
        const imageRes = await uploadImage(file).catch(e => {
          message.warn(e);
        });
        if (imageRes?.image?.id) {
          const item = {
            id: 'custom' as const,
            url: imageRes?.image?.raw?.full_url,
            thumbnail: imageRes?.image?.resize?.full_url,
          };
          setSelected(item);
          setUploadedFile({ ...item, file });
        }
      });
    },
    [uploadImage, t],
  );

  const imgList = useMemo(() => {
    return produce(list, draft => {
      draft?.map(x => {
        x.tokens = x.tokens.filter(
          t =>
            (t.content_type === 'image' || t.content_type === 'image_url') &&
            SUPPORTED_IMAGES.some(x => t.content?.toLowerCase().endsWith(x)),
        );

        return x;
      });
    }).filter(x => !!x.tokens?.length);
  }, [list]);

  const filteredCollectionList = useMemo(
    () =>
      searchText
        ? imgList.filter(x => x.name.toLowerCase().includes(searchText?.toLowerCase()))
        : imgList,
    [imgList, searchText],
  );

  const selectedCollection = useMemo(
    () => imgList?.find(x => x.id === selectedCollectionId),
    [imgList, selectedCollectionId],
  );

  const handleCollectionClick = useCallback(
    ({ currentTarget }: React.MouseEvent<HTMLDivElement>) => {
      const { id } = currentTarget.dataset;

      if (!id) {
        return;
      }

      showImgList();
      setSelectedCollectionId(id);
    },
    [showImgList],
  );

  const handleSearchChange = useCallback((text: string) => {
    setSearchText(text);
  }, []);

  const handleCancleClick = useCallback(() => {
    hideImgList();
  }, [hideImgList]);

  return (
    <CommonModal turnOff={onClose} containerClassName={style.avatarModal}>
      <div className={style.title}>{t('AvatarEditable_AvatarModal.i9pswe')}</div>
      <div className={style.main}>
        <div className={style.subTitle}>NFTs</div>
        <div className={style.searchWrap}>
          <SearchInput
            className={style.filterInput}
            placeholder={t('AvatarEditable_AvatarModal.ogm96e')}
            value={searchText}
            onChange={handleSearchChange}
            activeClassName
            allowClear
          />
          {imgListShown ? (
            <Button type="text" className={style.cancelBtn} onClick={handleCancleClick}>
              {t('AvatarEditable_AvatarModal.x9d2fu')}
            </Button>
          ) : null}
        </div>
        <div
          className={cx(style.collection, (!imgListShown || !selectedCollection) && style.hidden)}>
          <div className={style.collectionItem}>
            <div className={style.collectionName}>
              {selectedCollection?.name}
              {selectedCollection?.chain && selectedCollection?.chain !== 'eth' && (
                <img
                  className={style.collectionChain}
                  src={CHAINS_DICT[selectedCollection?.chain]?.logo}
                  alt="chain"
                />
              )}
            </div>
            <div className={style.tokens}>
              {selectedCollection?.tokens.map(item => (
                <CheckableIcon
                  key={item.id}
                  checked={selected.id === item.id}
                  data-url={item.content}
                  data-thumbnail={item.thumbnail_url}
                  data-id={item.id}
                  data-chain={item.chain}
                  data-type={item.content_type}
                  onClick={handleAvatarClick}>
                  <LazyMedia
                    src={item.content}
                    alt="nft content"
                    type={item.content_type}
                    className={style.innerWrap}
                  />
                </CheckableIcon>
              ))}
            </div>
          </div>
        </div>
        {isDone ? (
          imgList?.length ? (
            <div
              className={cx(
                style.collection,
                style.candidate,
                imgListShown && selectedCollection && style.hidden,
              )}>
              {filteredCollectionList?.map(x => (
                <div
                  className={cx(style.clctItem, x.id === selectedCollectionId && style.selected)}
                  data-id={x.id}
                  key={x.id}
                  onClick={handleCollectionClick}>
                  <LazyMedia className={style.clctIcon} src={x.logo_url} />
                  <div className={style.clctName}>
                    {x.name}
                    {x.chain && x.chain !== 'eth' && (
                      <img
                        className={style.collectionChain}
                        src={CHAINS_DICT[x.chain]?.logo}
                        alt="chain"
                      />
                    )}
                  </div>
                  <div className={style.clctAmount}>x{x.tokens?.length || 0} NFTs</div>
                </div>
              ))}
            </div>
          ) : (
            <div className={cx(style.collection, style.empty)}>
              <div className={style.emptyTitle}>{t('AvatarEditable_AvatarModal.emptyTip')}</div>
              <div className={style.emptyDesc}>{t('AvatarEditable_AvatarModal.emptyError')}</div>
            </div>
          )
        ) : (
          <div className={cx(style.collection, style.empty)}>
            <Loading />
          </div>
        )}

        <div className={style.customAvatarTitle}>
          {t('AvatarEditable_AvatarModal.mra288')}
          <div className={style.splitLine}></div>
          {enableCustomAvatarFeature ? null : (
            <ImgRcPaidFeature className={style.logo} onClick={gotoPaidFeature} />
          )}
        </div>
        <div className={style.customAvatar}>
          <CheckableIcon
            checked={selected.id === 'default'}
            data-id="default"
            data-url=""
            data-thumbnail=""
            onClick={handleAvatarClick}>
            <img src={isDark ? IconDefaultAvatarDark : IconDefaultAvatar} alt="" />
          </CheckableIcon>

          {isUploading ? (
            <div className={style.loadingPlaceholder}>
              <Loading />
            </div>
          ) : uploadedFile?.url ? (
            <CheckableIcon
              checked={selected.id === 'custom'}
              data-id="custom"
              data-url={uploadedFile.url}
              data-thumbnail={uploadedFile.thumbnail}
              onClick={handleAvatarClick}>
              <LazyMedia
                src={uploadedFile?.thumbnail || uploadedFile.url}
                className={style.customAvatarImage}
              />
            </CheckableIcon>
          ) : null}

          {enableCustomAvatarFeature ? (
            <ImageUpload placeholder={<IconUploadAvatar />} beforeUpload={handleBeforeUpload} />
          ) : (
            <HelperTooltip
              placement="right"
              overlayClassName={style.subscribeTooltip}
              title={
                <>
                  {t('AvatarEditable_AvatarModal.1co23nd')}{' '}
                  <span className={style.subscribeLink} onClick={gotoPaidFeature}>
                    {t('AvatarEditable_AvatarModal.1c37jyp')}
                  </span>
                </>
              }>
              <IconUploadAvatar />
            </HelperTooltip>
          )}
        </div>
      </div>
      <div className={style.footer}>
        <Button type="primary" className={style.btn} ghost onClick={onClose}>
          {t('AvatarEditable_AvatarModal.x9d2fu')}
        </Button>
        <Button
          type="primary"
          disabled={!selected.id}
          className={style.btn}
          loading={isUpdating}
          onClick={confirmChange}>
          {t('AvatarEditable_AvatarModal.1j2ql')}
        </Button>
      </div>
    </CommonModal>
  );
};

export default AvatarModal;
